import { Box, IconButton, VStack } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ForceGraph2D from 'react-force-graph-2d'
import { LuLocate, LuMaximize, LuMinus, LuPlus } from 'react-icons/lu'
import type { GraphData, GraphLink, GraphNode } from './hooks/useKnowledgeGraph'
import KnowledgeGraphPanel from './KnowledgeGraphPanel'

interface KnowledgeGraphProps {
  graphData: GraphData
  width: number
  height: number
}

interface ForceGraphHandle {
  zoomToFit: (duration?: number, padding?: number) => void
  centerAt: (x?: number, y?: number, duration?: number) => void
  zoom: (scale: number, duration?: number) => void
}

const HIGHLIGHT_COLOR = '#f59e0b'

export default function KnowledgeGraph({ graphData, width, height }: KnowledgeGraphProps) {
  const graphRef = useRef<ForceGraphHandle>(null)
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null)
  const hasZoomedRef = useRef(false)
  const currentZoomRef = useRef(1)
  const [layoutFrozen, setLayoutFrozen] = useState(false)

  // When the simulation engine stops for the first time, freeze the layout
  // so subsequent data updates don't move existing nodes.
  const handleEngineStop = useCallback(() => {
    if (layoutFrozen) {
      return
    }
    // Pin every node at its current position
    for (const node of graphData.nodes) {
      const n = node as any
      if (n.x != null) {
        n.fx = n.x
      }
      if (n.y != null) {
        n.fy = n.y
      }
    }
    setLayoutFrozen(true)
  }, [layoutFrozen, graphData.nodes])

  // Auto-zoom to fit once we have nodes
  useEffect(() => {
    if (graphData.nodes.length > 0 && !hasZoomedRef.current && graphRef.current) {
      setTimeout(() => {
        graphRef.current?.zoomToFit(400, 60)
        hasZoomedRef.current = true
      }, 1000)
    }
  }, [graphData.nodes.length])

  // Connected node IDs for hover highlighting
  const connectedIds = useMemo(() => {
    if (!hoveredNode) {
      return new Set<string>()
    }
    const ids = new Set<string>([hoveredNode.id])
    for (const link of graphData.links) {
      const src = typeof link.source === 'object' ? (link.source as GraphNode).id : link.source
      const tgt = typeof link.target === 'object' ? (link.target as GraphNode).id : link.target
      if (src === hoveredNode.id) {
        ids.add(tgt)
      }
      if (tgt === hoveredNode.id) {
        ids.add(src)
      }
    }
    return ids
  }, [hoveredNode, graphData.links])

  // Child nodes for the selected node panel
  const childNodes = useMemo(() => {
    if (!selectedNode) {
      return []
    }
    return graphData.nodes.filter((n) => {
      const parentPath = n.id.substring(0, n.id.lastIndexOf('/'))
      return parentPath === selectedNode.id
    })
  }, [selectedNode, graphData.nodes])

  const handleNodeClick = useCallback((node: any) => {
    setSelectedNode(node as GraphNode)
  }, [])

  // Double-click to zoom into a node
  const _handleNodeDoubleClick = useCallback((node: any) => {
    const g = graphRef.current
    if (!g) {
      return
    }
    g.centerAt(node.x, node.y, 600)
    g.zoom(4, 600)
    currentZoomRef.current = 4
  }, [])

  const handleNodeHover = useCallback((node: any) => {
    setHoveredNode(node as GraphNode | null)
  }, [])

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    const g = graphRef.current
    if (!g) {
      return
    }
    currentZoomRef.current = Math.min(currentZoomRef.current * 1.5, 20)
    g.zoom(currentZoomRef.current, 300)
  }, [])

  const handleZoomOut = useCallback(() => {
    const g = graphRef.current
    if (!g) {
      return
    }
    currentZoomRef.current = Math.max(currentZoomRef.current / 1.5, 0.1)
    g.zoom(currentZoomRef.current, 300)
  }, [])

  const handleFitAll = useCallback(() => {
    graphRef.current?.zoomToFit(400, 60)
  }, [])

  const handleCenterSelected = useCallback(() => {
    const g = graphRef.current
    if (!g || !selectedNode) {
      return
    }
    const node = graphData.nodes.find((n) => n.id === selectedNode.id)
    if (node?.x != null && node?.y != null) {
      g.centerAt(node.x, node.y, 600)
      g.zoom(3, 600)
      currentZoomRef.current = 3
    }
  }, [selectedNode, graphData.nodes])

  const nodeCanvasObject = useCallback(
    (node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const graphNode = node as GraphNode
      const x = node.x as number
      const y = node.y as number
      const isHighlighted = hoveredNode && connectedIds.has(graphNode.id)
      const isHovered = hoveredNode?.id === graphNode.id
      const isSelected = selectedNode?.id === graphNode.id

      // Draw node circle
      ctx.beginPath()
      ctx.arc(x, y, graphNode.size, 0, 2 * Math.PI)
      ctx.fillStyle = isSelected ? HIGHLIGHT_COLOR : graphNode.color
      ctx.fill()

      if (isHovered || isSelected) {
        ctx.strokeStyle = HIGHLIGHT_COLOR
        ctx.lineWidth = 2
        ctx.stroke()
      } else if (isHighlighted) {
        ctx.strokeStyle = graphNode.color
        ctx.lineWidth = 1
        ctx.stroke()
      }

      // Always draw label, scale font to remain readable at any zoom
      const fontSize = Math.max(10 / globalScale, 2)
      ctx.font = `${fontSize}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.fillStyle = hoveredNode && !isHighlighted ? 'rgba(100,100,100,0.3)' : '#374151'
      ctx.fillText(graphNode.label, x, y + graphNode.size + 2)
    },
    [hoveredNode, connectedIds, selectedNode],
  )

  const nodePointerAreaPaint = useCallback(
    (node: any, color: string, ctx: CanvasRenderingContext2D) => {
      const graphNode = node as GraphNode
      ctx.beginPath()
      ctx.arc(node.x as number, node.y as number, graphNode.size + 2, 0, 2 * Math.PI)
      ctx.fillStyle = color
      ctx.fill()
    },
    [],
  )

  const linkColor = useCallback(
    (link: any) => {
      if (!hoveredNode) {
        return 'rgba(100,116,139,0.6)'
      }
      const graphLink = link as GraphLink
      const src =
        typeof graphLink.source === 'object' ? (graphLink.source as GraphNode).id : graphLink.source
      const tgt =
        typeof graphLink.target === 'object' ? (graphLink.target as GraphNode).id : graphLink.target
      if (connectedIds.has(src) && connectedIds.has(tgt)) {
        return 'rgba(245,158,11,1)'
      }
      return 'rgba(100,116,139,0.15)'
    },
    [hoveredNode, connectedIds],
  )

  const linkWidth = useCallback(
    (link: any) => {
      if (!hoveredNode) {
        return 1
      }
      const graphLink = link as GraphLink
      const src =
        typeof graphLink.source === 'object' ? (graphLink.source as GraphNode).id : graphLink.source
      const tgt =
        typeof graphLink.target === 'object' ? (graphLink.target as GraphNode).id : graphLink.target
      if (connectedIds.has(src) && connectedIds.has(tgt)) {
        return 2.5
      }
      return 0.5
    },
    [hoveredNode, connectedIds],
  )

  const graphWidth = selectedNode ? width - 320 : width

  return (
    <Box position="relative" w={width} h={height}>
      <ForceGraph2D
        ref={graphRef as any}
        graphData={graphData}
        width={graphWidth}
        height={height}
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={nodePointerAreaPaint}
        onNodeClick={handleNodeClick}
        onNodeHover={handleNodeHover}
        onBackgroundClick={handleBackgroundClick}
        linkColor={linkColor}
        linkWidth={linkWidth}
        linkDirectionalArrowLength={3}
        linkDirectionalArrowRelPos={1}
        linkDirectionalParticles={2}
        linkDirectionalParticleWidth={2}
        linkDirectionalParticleSpeed={0.005}
        d3AlphaDecay={0.1}
        d3VelocityDecay={0.5}
        // @ts-expect-error d3ReheatSimulation exists at runtime but is missing from type defs
        d3ReheatSimulation={!layoutFrozen}
        warmupTicks={100}
        cooldownTicks={200}
        onEngineStop={handleEngineStop}
        enableNodeDrag={true}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        onNodeDragEnd={(node: any) => {
          node.fx = node.x
          node.fy = node.y
        }}
      />

      {/* Zoom controls */}
      <VStack position="absolute" bottom={4} left={4} gap={1} zIndex={10}>
        <IconButton
          aria-label="Zoom in"
          size="sm"
          variant="outline"
          bg="bg.panel"
          onClick={handleZoomIn}
        >
          <LuPlus />
        </IconButton>
        <IconButton
          aria-label="Zoom out"
          size="sm"
          variant="outline"
          bg="bg.panel"
          onClick={handleZoomOut}
        >
          <LuMinus />
        </IconButton>
        <IconButton
          aria-label="Fit all"
          size="sm"
          variant="outline"
          bg="bg.panel"
          onClick={handleFitAll}
        >
          <LuMaximize />
        </IconButton>
        {selectedNode && (
          <IconButton
            aria-label="Center on selected"
            size="sm"
            variant="outline"
            bg="bg.panel"
            onClick={handleCenterSelected}
          >
            <LuLocate />
          </IconButton>
        )}
      </VStack>

      {selectedNode && (
        <KnowledgeGraphPanel
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          childNodes={childNodes}
        />
      )}
    </Box>
  )
}
